import { useTenderDeadline } from "../../../../../hooks/useTenderDeadline";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import style from "./OrderBidBlock.module.scss";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import UpdateBidModal from "./UpdateBidModal";
import RevokeBidModal from "./RevokeBidModal";
import { TenderDeadlineText } from "../../../../shared/TenderDeadlineText/TenderDeadlineText";
import HintIcon from "../../../../shared/icons/HintIcon";
import CanceledIcon from "../../../../shared/icons/CanceledIcon";
import { CURRENCIES } from "../../../../../mock";
import { useSelector } from "react-redux";
import RebidNotice from "../../../order-info/components/RebidNotice/RebidNotice";

const formatPrice = (value, currency = "KZT") => {
  if (value === null || value === undefined) return "-";
  const formatted = Number(value).toLocaleString("ru-RU").replace(/,/g, " ");
  const resolvedCurrency = currency || "KZT";
  const symbol = resolvedCurrency === "KZT" ? "₸" : CURRENCIES[resolvedCurrency]?.symbol || resolvedCurrency;
  return `${formatted} ${symbol}`;
};

const OrderBidBlock = ({
  onRefreshOrder,
  bid,
  order,
  isCanceled = false,
  canceledText = "",
  onUpdateData,
  onFindNewOrders,
}) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(order?.tenderDateEnd);
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);

  if (!bid) return null;

  const isLeading = bid.position === 1;
  const isTenderEnded = order?.tenderDateEnd && isTenderExpiredByServer;
  const isOtherExpeditorAssigned = !!order?.expeditorId && order.expeditorId !== user?.companyId;

  return (
    <div className={style.card}>
      {order?.isInRebid === true && !isTenderEnded && (
        <RebidNotice>
          {t(
            order?.assignmentMethod === "Auction"
              ? "orderInfoExpeditor.bid.auctionRebidNotice"
              : "orderInfoExpeditor.bid.rebidNotice",
          )}
        </RebidNotice>
      )}

      <div className={style.topbar}>
        <div className={style.topbar_left}>
          <span className={style.your_bid_label}>{t("orderInfoExpeditor.bid.yourBid")}</span>
          <span className={style.your_bid_price}>{formatPrice(bid.price, bid.currency)}</span>
        </div>
        {order?.tenderDateEnd && (
          <div className={style.topbar_right}>
            <span className={style.deadline_label}>
              {t(
                order?.isInRebid === true
                  ? "orderInfoExpeditor.bid.rebidClosingDeadline"
                  : order?.assignmentMethod === "Auction"
                    ? "orderInfoExpeditor.bid.auctionClosingDeadline"
                    : "orderInfoExpeditor.bid.tenderClosingDeadline",
              )}
            </span>
            <span className={style.deadline_value}><TenderDeadlineText date={order.tenderDateEnd} /></span>
          </div>
        )}
      </div>

      {order?.assignmentMethod === "Auction" && !bid.isMultiCurrency && !isOtherExpeditorAssigned && (
        <div className={style.stats_row}>
          <div className={style.stat_cell}>
            <span className={style.stat_label}>{t("orderInfoExpeditor.bid.bestBid")}</span>
            <span className={style.stat_value}>{formatPrice(bid.minPrice, bid.currency)}</span>
          </div>
          {/* <div className={style.stat_divider} /> */}
          <div className={style.stat_cell}>
            <span className={style.stat_label}>{t("orderInfoExpeditor.bid.yourPlace")}</span>
            <span className={isLeading ? style.stat_value_lead : style.stat_value_behind}>
              {t("orderInfoExpeditor.bid.position", { position: bid.position, total: bid.totalBids })}
            </span>
          </div>
        </div>
      )}

      {isCanceled ? (
        <div className={style.hint_box_danger}>
          <CanceledIcon />
          <span className={style.hint_text_danger}>{canceledText}</span>
        </div>
      ) : isOtherExpeditorAssigned ? (
        <div className={style.hint_box_danger}>
          <HintIcon color="#E5373C" />
          <span className={style.hint_text_danger}>
            {t(
              order?.assignmentMethod === "Auction"
                ? "orderInfoExpeditor.bid.auctionEnded"
                : "orderInfoExpeditor.bid.tenderEnded",
            )}
          </span>
        </div>
      ) : isTenderEnded ? (
        <div className={style.hint_box_error}>
          <HintIcon />
          <span className={style.hint_text_error}>
            {t(order?.assignmentMethod === "Auction"
              ? "orderInfoExpeditor.bid.auctionCompletedAwaitingDecision"
              : "orderInfoExpeditor.bid.tenderCompletedAwaitingDecision")}
          </span>
        </div>
      ) : order?.assignmentMethod === "Auction" && !isLeading && !bid.isMultiCurrency && (
        <div className={style.hint_box}>
          <HintIcon />
          <span className={style.hint_text}>{t("orderInfoExpeditor.bid.lowerBidHint")}</span>
        </div>
      )}

      {isTenderEnded || isOtherExpeditorAssigned || isCanceled ? (
        <div className={style.buttons_row}>
          <CustomButton
            text={t("orderInfoExpeditor.bid.findNewOrders")}
            isNavy
            onClick={onFindNewOrders}
            style={{ width: "100%" }}
          />
        </div>
      ) : (
        <div className={style.buttons_row}>
          <div className={style.btn_primary}>
            <CustomButton
              text={t("orderInfoExpeditor.bid.changeBid")}
              isNavy
              onClick={() => setUpdateModalOpen(true)}
              style={{ width: "100%" }}
            />
          </div>
          <div className={style.btn_secondary}>
            <CustomButton
              text={t("orderInfoExpeditor.bid.revokeBid")}
              isNavy
              isWhite
              onClick={() => setRevokeModalOpen(true)}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      )}

      <UpdateBidModal
        onRefreshOrder={onRefreshOrder}
        isOpen={updateModalOpen && !isTenderEnded}
        onClose={() => setUpdateModalOpen(false)}
        bid={bid}
        order={order}
        onSuccess={() => {
          setUpdateModalOpen(false);
          if (onUpdateData) onUpdateData();
        }}
      />

      <RevokeBidModal
        onRefreshOrder={onRefreshOrder}
        order={order}
        isOpen={revokeModalOpen && !isTenderEnded}
        onClose={() => setRevokeModalOpen(false)}
        bid={bid}
        onSuccess={() => {
          setRevokeModalOpen(false);
          if (onUpdateData) onUpdateData();
        }}
      />
    </div>
  );
};

export default OrderBidBlock;
